<template>
  <div v-if="isVisible" class="quick-actions-fab-container">
    <!-- Backdrop overlay when menu is open -->
    <Transition name="fab-backdrop-fade">
      <div
        v-if="isOpen"
        class="quick-actions-backdrop"
        aria-hidden="true"
        @click="closeMenu"
      />
    </Transition>

    <!-- Speed Dial Menu Panel -->
    <Transition name="fab-menu-pop">
      <div
        v-if="isOpen"
        class="quick-actions-menu"
        role="dialog"
        :aria-label="$t('quickActions')"
      >
        <div class="quick-actions-menu__header">
          <div class="quick-actions-menu__title-wrap">
            <span class="quick-actions-menu__badge">
              <i class="bi bi-lightning-charge-fill" aria-hidden="true"></i>
            </span>
            <div>
              <h6 class="quick-actions-menu__title mb-0">{{ $t("quickActions") }}</h6>
              <div class="quick-actions-menu__policy-info">
                <span class="policy-dot" :class="effectivePolicyId ? 'policy-dot--active' : 'policy-dot--warn'"></span>
                <span v-if="effectivePolicyId" class="policy-text">
                  {{ $t("quickActionActivePolicy") }}: <strong>{{ activePolicyLabel }}</strong>
                </span>
                <span v-else class="policy-text policy-text--warn">
                  {{ $t("navSelectPolicyFirst") }}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="quick-actions-menu__close-btn"
            :aria-label="$t('commonClose')"
            @click="closeMenu"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Policy Selector Dropdown if multiple policies available -->
        <div v-if="availablePolicies.length > 1" class="quick-actions-policy-selector mb-2">
          <el-select
            v-model="selectedPolicyId"
            size="small"
            class="w-100"
            :placeholder="$t('quickActionSelectPolicy')"
            @change="onPolicyChanged"
          >
            <el-option
              v-for="pol in availablePolicies"
              :key="pol.policyId"
              :label="`${pol.policySeries || ''}${pol.policyNumber || ''} (${formatDate(pol.startDate)} - ${formatDate(pol.endDate)})`"
              :value="pol.policyId"
            />
          </el-select>
        </div>

        <div class="quick-actions-menu__list">
          <!-- 1. Change Program -->
          <button
            type="button"
            class="quick-action-item quick-action-item--warning"
            @click="handleAction('programChange')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--warning">
              <i class="bi bi-arrow-left-right" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="quick-action-item__title">{{ $t("quickActionChangeProgram") }}</div>
              <div class="quick-action-item__desc">{{ $t("quickActionChangeProgramDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>

          <!-- 2. Add Insured Person -->
          <button
            type="button"
            class="quick-action-item quick-action-item--success"
            @click="handleAction('addInsured')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--success">
              <i class="bi bi-person-plus-fill" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="quick-action-item__title">{{ $t("quickActionAddInsured") }}</div>
              <div class="quick-action-item__desc">{{ $t("quickActionAddInsuredDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>

          <!-- 3. Import Excel File -->
          <button
            type="button"
            class="quick-action-item quick-action-item--info"
            @click="handleAction('importExcel')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--info">
              <i class="bi bi-file-earmark-spreadsheet-fill" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="quick-action-item__title">{{ $t("quickActionImportExcel") }}</div>
              <div class="quick-action-item__desc">{{ $t("quickActionImportExcelDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>

          <!-- 4. Terminate / Imtina -->
          <button
            type="button"
            class="quick-action-item quick-action-item--danger"
            @click="handleAction('terminate')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--danger">
              <i class="bi bi-x-circle-fill" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="quick-action-item__title">{{ $t("quickActionTerminate") }}</div>
              <div class="quick-action-item__desc">{{ $t("quickActionTerminateDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>

          <!-- 5. Upload File to Policy -->
          <button
            type="button"
            class="quick-action-item quick-action-item--primary"
            @click="handleAction('uploadFile')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--primary">
              <i class="bi bi-cloud-arrow-up-fill" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="quick-action-item__title">{{ $t("quickActionUploadFile") }}</div>
              <div class="quick-action-item__desc">{{ $t("quickActionUploadFileDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>

          <!-- 6. Support Chat (In Development) -->
          <button
            type="button"
            class="quick-action-item quick-action-item--teal"
            @click="handleAction('chat')"
          >
            <div class="quick-action-item__icon quick-action-item__icon--teal">
              <i class="bi bi-chat-dots-fill" aria-hidden="true"></i>
            </div>
            <div class="quick-action-item__content">
              <div class="d-flex align-items-center gap-1">
                <span class="quick-action-item__title">{{ $t("quickActionChat") }}</span>
                <span class="badge badge--warning text-2xs px-1.5 py-0.5 rounded-pill">{{ $t("chatInDevelopmentBadge") }}</span>
              </div>
              <div class="quick-action-item__desc">{{ $t("quickActionChatDesc") }}</div>
            </div>
            <i class="bi bi-chevron-right quick-action-item__arrow" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </Transition>

    <!-- Main Trigger Floating Button -->
    <button
      type="button"
      class="quick-actions-fab-btn"
      :class="{ 'quick-actions-fab-btn--open': isOpen }"
      :title="$t('quickActionsTooltip')"
      :aria-label="$t('quickActions')"
      :aria-expanded="isOpen"
      @click="toggleMenu"
    >
      <div class="quick-actions-fab-icon-wrap">
        <i class="bi bi-lightning-charge-fill icon-closed" aria-hidden="true"></i>
        <i class="bi bi-x-lg icon-opened" aria-hidden="true"></i>
      </div>
      <span class="quick-actions-fab-pulse" aria-hidden="true"></span>
    </button>

    <!-- 1 & 4. REQUEST OPERATION MODAL (Program Change & Terminate) -->
    <request-operation-modal
      v-model="opDialogVisible"
      :op-type="opType"
      :policy-id="effectivePolicyId"
      @success="onOperationSuccess"
    />

    <!-- 2. ADD INSURED MODAL (Dual Search + Registration) -->
    <Teleport to="body">
      <div
        v-if="addPersonDialogVisible"
        class="account-dual-root"
        @keydown.esc.prevent="closeAddPersonDialog"
      >
        <div class="account-dual-root__mask" aria-hidden="true" @click="closeAddPersonDialog" />
        <div class="account-dual-root__stage">
          <AccountPersonSearchDialog
            v-model:pin="personSearch.pin"
            v-model:doc-series="personSearch.docSeries"
            v-model:doc-number="personSearch.docNumber"
            v-model:search-mode="personSearch.searchMode"
            embedded
            :model-value="addPersonDialogVisible"
            :loading="personSearch.loading"
            @search="triggerPersonSearch"
          />

          <div
            class="account-insured-dialog-panel account-insured-dialog-panel--main account-dual-main-card"
            role="dialog"
            aria-modal="true"
            :aria-label="$t('requestNewInsured')"
          >
            <div class="account-dual-header">
              <span class="account-dual-header__title">{{ $t("requestNewInsured") }}</span>
              <button
                type="button"
                class="account-dual-header__close"
                :aria-label="$t('commonClose')"
                @click="closeAddPersonDialog"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div class="account-dual-main-body">
              <InsuredDialog
                ref="insuredFormRef"
                :policy-id="effectivePolicyId"
                :search-draft="personSearch"
                @update:search-draft="personSearch = $event"
                @custom-event="closeAddPersonDialog"
                @custom-insert="onPersonInserted"
              />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 3. IMPORT EXCEL DIALOG -->
    <el-dialog
      v-if="excelDialogVisible"
      v-model="excelDialogVisible"
      :close-on-click-modal="false"
      :title="$t('requestNewImport')"
      modal-class="account-insured-overlay"
      class="account-insured-dialog-panel account-insured-dialog-panel--import"
      width="98vw"
      top="1.5vh"
      :destroy-on-close="true"
      append-to-body
    >
      <InsuredImportDialog
        :policy-id="effectivePolicyId"
        :policy-end-date="activePolicy?.endDate"
        @custom-event="excelDialogVisible = false"
        @custom-insert="onExcelImported"
      />
    </el-dialog>

    <!-- 5. QUICK FILE UPLOAD DIALOG -->
    <el-dialog
      v-model="fileUploadVisible"
      :title="$t('quickActionFileUploadTitle')"
      modal-class="modal-class"
      width="620px"
      :destroy-on-close="true"
      align-center
      append-to-body
    >
      <div class="quick-file-upload-body">
        <div class="quick-file-policy-pill mb-3 p-2 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-file-earmark-text-fill text-primary fs-5"></i>
            <div>
              <div class="fw-bold text-sm text-dark dark-text-white">{{ activePolicyLabel }}</div>
              <div class="text-xs text-muted">{{ $t("quickActionActivePolicy") }}</div>
            </div>
          </div>
          <router-link
            v-if="effectivePolicyId"
            :to="`/files?id=${effectivePolicyId}`"
            class="action-btn action-btn--outline action-btn--compact mb-0"
            @click="fileUploadVisible = false"
          >
            <i class="bi bi-folder2-open me-1"></i>
            <span>{{ $t("quickActionViewAllFiles") }}</span>
          </router-link>
        </div>

        <el-upload
          class="quick-file-dropzone"
          drag
          :action="fileurl"
          :on-success="onFileUploadSuccess"
          :on-error="onFileUploadError"
          multiple
        >
          <div class="quick-file-dropzone__content">
            <i class="bi bi-cloud-arrow-up-fill quick-file-dropzone__icon"></i>
            <div class="quick-file-dropzone__title">
              {{ $t("quickActionDropFilesPrompt") }}
            </div>
            <div class="quick-file-dropzone__hint text-xs text-muted">
              PDF, DOCX, XLSX, PNG, JPG
            </div>
          </div>
        </el-upload>
      </div>
      <template #footer>
        <div class="dialog-footer d-flex justify-content-end">
          <button
            type="button"
            class="action-btn action-btn--muted action-btn--compact mb-0"
            @click="fileUploadVisible = false"
          >
            {{ $t("commonClose") }}
          </button>
        </div>
      </template>
    </el-dialog>

    <!-- 6. CHAT IN DEVELOPMENT DIALOG -->
    <el-dialog
      v-model="chatDialogVisible"
      :title="$t('chatInDevelopmentTitle')"
      modal-class="modal-class"
      width="520px"
      :destroy-on-close="true"
      align-center
      append-to-body
    >
      <div class="quick-chat-modal-body text-center py-4 px-2">
        <div class="quick-chat-icon-wrapper mb-3">
          <div class="quick-chat-icon-pulse"></div>
          <div class="quick-chat-icon-circle">
            <i class="bi bi-chat-dots-fill text-info"></i>
          </div>
        </div>

        <div class="mb-2">
          <span class="badge badge--warning text-xs px-3 py-1 rounded-pill">
            <i class="bi bi-tools me-1"></i>
            {{ $t("chatInDevelopmentBadge") }}
          </span>
        </div>

        <h5 class="fw-bold mt-3 mb-2 text-dark dark-text-white">
          {{ $t("chatInDevelopmentTitle") }}
        </h5>

        <p class="text-sm text-muted mb-4 px-3" style="line-height: 1.6;">
          {{ $t("chatInDevelopmentDesc") }}
        </p>

        <!-- Alternative Contact Channels -->
        <div class="quick-chat-channels p-3 rounded-3 text-start">
          <div class="text-xs fw-bold text-muted mb-2 text-uppercase tracking-wider">
            {{ $t("commonContacts") || "Əlaqə vasitələri" }}
          </div>
          <div class="d-flex align-items-center justify-content-between py-1 border-bottom">
            <span class="text-xs text-muted d-flex align-items-center gap-2">
              <i class="bi bi-telephone-fill text-primary"></i>
              <span>Qaynar xətt</span>
            </span>
            <strong class="text-xs text-dark dark-text-white">*8833 / 180</strong>
          </div>
          <div class="d-flex align-items-center justify-content-between py-1 pt-2">
            <span class="text-xs text-muted d-flex align-items-center gap-2">
              <i class="bi bi-envelope-fill text-success"></i>
              <span>E-poçt</span>
            </span>
            <strong class="text-xs text-dark dark-text-white">support@pasha-life.az</strong>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer d-flex justify-content-end">
          <button
            type="button"
            class="action-btn action-btn--muted action-btn--compact mb-0"
            @click="chatDialogVisible = false"
          >
            {{ $t("commonClose") }}
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getPolicyId, getRouteSection } from "@/project/Source/routes";
import Source from "@/project/Source/claim.js";
import CryptoJS from "crypto-js";
import { fileurl, swalDialogClass } from "@/project/Source/helper.js";
import { notifyRequestSuccess } from "@/project/Source/utils.js";
import RequestOperationModal from "@/project/Request/components/RequestOperationModal.vue";
import AccountPersonSearchDialog from "@/project/Request/components/AccountPersonSearchDialog.vue";
import InsuredDialog from "@/project/Request/components/InsuredDialog.vue";
import InsuredImportDialog from "@/project/Request/components/InsuredImportDialog.vue";

export default {
  name: "QuickActionsFab",
  components: {
    RequestOperationModal,
    AccountPersonSearchDialog,
    InsuredDialog,
    InsuredImportDialog,
  },
  data() {
    return {
      isOpen: false,
      fileurl,
      opDialogVisible: false,
      opType: "programChange",
      addPersonDialogVisible: false,
      excelDialogVisible: false,
      fileUploadVisible: false,
      chatDialogVisible: false,
      availablePolicies: [],
      selectedPolicyId: null,
      activePolicy: null,
      personSearch: {
        pin: "",
        docSeries: "",
        docNumber: "",
        searchMode: "asan",
        loading: false,
      },
    };
  },
  computed: {
    isVisible() {
      const section = getRouteSection(this.$route);
      return section !== "signIn" && section !== "";
    },
    effectivePolicyId() {
      if (this.selectedPolicyId) {
        return Number(this.selectedPolicyId);
      }
      const fromRoute = getPolicyId(this.$route);
      if (fromRoute) {
        return Number(fromRoute);
      }
      try {
        const stored = sessionStorage.getItem("policy");
        if (stored && stored !== "none" && stored !== "null") {
          return Number(JSON.parse(stored));
        }
      } catch {
        // ignore error
      }
      if (this.availablePolicies.length > 0) {
        return Number(this.availablePolicies[0].policyId);
      }
      return null;
    },
    activePolicyLabel() {
      try {
        const stored = sessionStorage.getItem("policyNumber");
        if (stored && stored !== "none" && stored !== "null") {
          const number = JSON.parse(stored);
          if (number) return number;
        }
      } catch {
        // ignore
      }
      if (this.activePolicy) {
        return `${this.activePolicy.policySeries || ""}${this.activePolicy.policyNumber || ""}`;
      }
      const current = this.availablePolicies.find((p) => p.policyId === this.effectivePolicyId);
      if (current) {
        return `${current.policySeries || ""}${current.policyNumber || ""}`;
      }
      return this.effectivePolicyId ? String(this.effectivePolicyId) : "---";
    },
  },
  watch: {
    "$route.fullPath": {
      handler() {
        this.syncPolicyFromContext();
      },
    },
    effectivePolicyId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadPolicyDetails(val);
        }
      },
    },
  },
  mounted() {
    this.fetchCompanyPolicies();
    this.syncPolicyFromContext();

    // Close on Escape key
    this.onKeyDown = (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.closeMenu();
      }
    };
    window.addEventListener("keydown", this.onKeyDown);
  },
  beforeUnmount() {
    if (this.onKeyDown) {
      window.removeEventListener("keydown", this.onKeyDown);
    }
  },
  methods: {
    toggleMenu() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.syncPolicyFromContext();
      }
    },
    closeMenu() {
      this.isOpen = false;
    },
    syncPolicyFromContext() {
      const fromRoute = getPolicyId(this.$route);
      if (fromRoute) {
        this.selectedPolicyId = Number(fromRoute);
        return;
      }
      try {
        const stored = sessionStorage.getItem("policy");
        if (stored && stored !== "none" && stored !== "null") {
          this.selectedPolicyId = Number(JSON.parse(stored));
        }
      } catch {
        // ignore
      }
    },
    fetchCompanyPolicies() {
      try {
        const rawUser = window.localStorage.getItem("user");
        if (!rawUser) return;
        const decrypted = CryptoJS.AES.decrypt(rawUser, "secret_key").toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(decrypted);
        if (!userData || !userData.companyId) return;

        Source.MedicalPolicyByCompany({
          id: userData.companyId,
          requestCorrelationId: "string",
        })
          .then((res) => {
            this.availablePolicies = res.data?.data || [];
            if (!this.selectedPolicyId && this.availablePolicies.length > 0) {
              const currentId = this.effectivePolicyId || this.availablePolicies[0].policyId;
              this.selectedPolicyId = currentId;
            }
          })
          .catch(() => {
            this.availablePolicies = [];
          });
      } catch (e) {
        console.error("Error fetching company policies:", e);
      }
    },
    loadPolicyDetails(policyId) {
      if (!policyId) return;
      Source.Policy({ id: policyId, requestCorrelationId: "string" })
        .then((res) => {
          this.activePolicy = res.data?.data?.[0] || null;
        })
        .catch(() => {
          this.activePolicy = null;
        });
    },
    onPolicyChanged(val) {
      this.selectedPolicyId = val;
      const found = this.availablePolicies.find((p) => p.policyId === val);
      if (found) {
        this.activePolicy = found;
        sessionStorage.setItem("policy", JSON.stringify(found.policyId));
        sessionStorage.setItem(
          "policyNumber",
          JSON.stringify(`${found.policySeries || ""}${found.policyNumber || ""}`)
        );
      }
    },
    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      if (isNaN(date)) return "";
      const day = ("0" + date.getDate()).slice(-2);
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },
    handleAction(action) {
      this.closeMenu();

      if (action === "chat") {
        this.chatDialogVisible = true;
        return;
      }

      if (!this.effectivePolicyId) {
        this.$swal({
          title: this.$t("navSelectPolicyFirst"),
          icon: "warning",
          customClass: swalDialogClass(),
          buttonsStyling: false,
        });
        return;
      }

      // Ensure active policy is stored for components reading from sessionStorage
      sessionStorage.setItem("policy", JSON.stringify(this.effectivePolicyId));
      if (this.activePolicyLabel && this.activePolicyLabel !== "---") {
        sessionStorage.setItem("policyNumber", JSON.stringify(this.activePolicyLabel));
      }

      if (action === "programChange") {
        this.opType = "programChange";
        this.opDialogVisible = true;
      } else if (action === "addInsured") {
        this.resetPersonSearch();
        this.addPersonDialogVisible = true;
      } else if (action === "importExcel") {
        this.excelDialogVisible = true;
      } else if (action === "terminate") {
        this.opType = "terminate";
        this.opDialogVisible = true;
      } else if (action === "uploadFile") {
        this.fileUploadVisible = true;
      }
    },
    resetPersonSearch() {
      this.personSearch = {
        pin: "",
        docSeries: "",
        docNumber: "",
        searchMode: "asan",
        loading: false,
      };
    },
    closeAddPersonDialog() {
      this.addPersonDialogVisible = false;
      this.resetPersonSearch();
    },
    triggerPersonSearch() {
      this.$refs.insuredFormRef?.searchPerson?.();
    },
    notifyGlobalRefresh(detail) {
      window.dispatchEvent(new CustomEvent("app:refresh-data", { detail }));
    },
    onOperationSuccess() {
      this.notifyGlobalRefresh({ type: this.opType });
    },
    onPersonInserted() {
      this.closeAddPersonDialog();
      notifyRequestSuccess({
        count: 1,
        policyId: this.effectivePolicyId,
        isBulk: false,
      });
      this.notifyGlobalRefresh({ type: "addInsured", isBulk: false });
    },
    onExcelImported() {
      this.excelDialogVisible = false;
      notifyRequestSuccess({
        policyId: this.effectivePolicyId,
        isBulk: true,
      });
      this.notifyGlobalRefresh({ type: "importExcel", isBulk: true });
    },
    onFileUploadSuccess() {
      this.$swal({
        title: this.$t("operationSuccess"),
        text: this.$t("quickActionFileUploadSuccess"),
        icon: "success",
        customClass: swalDialogClass(),
        buttonsStyling: false,
      });
      this.notifyGlobalRefresh({ type: "fileUpload" });
    },
    onFileUploadError() {
      this.$swal({
        title: this.$t("operationFailed"),
        text: this.$t("quickActionFileUploadError"),
        icon: "error",
        customClass: swalDialogClass(),
        buttonsStyling: false,
      });
    },
  },
};
</script>

<style scoped>
.quick-actions-fab-container {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* Semi-transparent backdrop for speed dial */
.quick-actions-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1040;
}

/* Floating Action Button */
.quick-actions-fab-btn {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: linear-gradient(135deg, #0b4f9c 0%, #1a6fc4 100%);
  color: #ffffff;
  font-size: 1.38rem;
  box-shadow: 0 8px 20px -3px rgba(11, 79, 156, 0.45), 0 4px 6px -2px rgba(11, 79, 156, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1051;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  outline: none;
}

.quick-actions-fab-btn:hover {
  transform: scale(1.08) translateY(-2px);
  box-shadow: 0 14px 28px -2px rgba(11, 79, 156, 0.5), 0 6px 10px -2px rgba(11, 79, 156, 0.3);
  background: linear-gradient(135deg, #094080 0%, #155ea8 100%);
}

.quick-actions-fab-btn:active {
  transform: scale(0.96);
}

.quick-actions-fab-btn--open {
  background: linear-gradient(135deg, #e11d48 0%, #f43f5e 100%);
  box-shadow: 0 10px 25px -3px rgba(225, 29, 72, 0.4);
}

.quick-actions-fab-btn--open:hover {
  background: linear-gradient(135deg, #be123c 0%, #e11d48 100%);
  box-shadow: 0 14px 28px -2px rgba(225, 29, 72, 0.5);
}

.quick-actions-fab-icon-wrap {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-closed,
.icon-opened {
  position: absolute;
  font-size: 1.8rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.icon-opened {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

.quick-actions-fab-btn--open .icon-closed {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

.quick-actions-fab-btn--open .icon-opened {
  opacity: 1;
  transform: rotate(0) scale(1);
}

/* Subtle Pulse Glow */
.quick-actions-fab-pulse {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(11, 79, 156, 0.4);
  animation: fab-pulse 2.8s cubic-bezier(0.24, 0, 0.38, 1) infinite;
  pointer-events: none;
}

.quick-actions-fab-btn--open .quick-actions-fab-pulse {
  display: none;
}

@keyframes fab-pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.25);
    opacity: 0;
  }
  100% {
    transform: scale(1.35);
    opacity: 0;
  }
}

/* Speed Dial Menu Card */
.quick-actions-menu {
  position: absolute;
  bottom: 84px;
  right: 0;
  width: 360px;
  max-width: calc(100vw - 32px);
  background: var(--app-surface-elevated, #ffffff);
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 16px;
  box-shadow: 0 20px 40px -8px rgba(15, 23, 42, 0.2), 0 8px 16px -4px rgba(15, 23, 42, 0.1);
  padding: 16px;
  z-index: 1051;
  transform-origin: bottom right;
  backdrop-filter: blur(16px);
}

.quick-actions-menu__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--app-border, #e5e7eb);
}

.quick-actions-menu__title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quick-actions-menu__badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(11, 79, 156, 0.15) 0%, rgba(26, 111, 196, 0.25) 100%);
  color: var(--bs-info, #0b4f9c);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.quick-actions-menu__title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--app-heading, #111827);
  letter-spacing: -0.01em;
}

.quick-actions-menu__policy-info {
  font-size: 0.75rem;
  color: var(--app-text, #6b7280);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 1px;
}

.policy-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.policy-dot--active {
  background-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.policy-dot--warn {
  background-color: #f59e0b;
}

.policy-text--warn {
  color: #d97706;
  font-weight: 600;
}

.quick-actions-menu__close-btn {
  background: transparent;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: var(--app-text, #9ca3af);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-actions-menu__close-btn:hover {
  background: var(--app-surface-muted, #f3f4f6);
  color: var(--app-heading, #111827);
}

/* Action Items List */
.quick-actions-menu__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quick-action-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  gap: 12px;
}

.quick-action-item:hover {
  background: var(--app-surface-soft, #f8fafc);
  border-color: var(--app-border, #e2e8f0);
  transform: translateX(-3px);
}

.quick-action-item__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
  transition: transform 0.22s ease;
}

.quick-action-item:hover .quick-action-item__icon {
  transform: scale(1.08);
}

.quick-action-item__icon--warning {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.quick-action-item__icon--success {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.quick-action-item__icon--info {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.quick-action-item__icon--danger {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.quick-action-item__icon--primary {
  background: rgba(139, 92, 246, 0.12);
  color: #7c3aed;
}

.quick-action-item__icon--teal {
  background: rgba(14, 165, 233, 0.12);
  color: #0284c7;
}

.quick-action-item--teal:hover {
  background: rgba(14, 165, 233, 0.06);
}

.quick-chat-icon-wrapper {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-chat-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(14, 165, 233, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  z-index: 1;
}

.quick-chat-icon-pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(14, 165, 233, 0.25);
  animation: chat-pulse 2s infinite ease-in-out;
}

@keyframes chat-pulse {
  0% {
    transform: scale(0.85);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.18);
    opacity: 0.2;
  }
  100% {
    transform: scale(0.85);
    opacity: 0.8;
  }
}

.quick-chat-channels {
  background: var(--app-surface-soft, rgba(120, 130, 145, 0.06));
  border: 1px solid var(--app-border, #e2e8f0);
}

.text-2xs {
  font-size: 0.6875rem;
}

.badge--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.quick-action-item__content {
  flex: 1;
  min-width: 0;
}

.quick-action-item__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--app-heading, #1e293b);
  line-height: 1.25;
}

.quick-action-item__desc {
  font-size: 0.735rem;
  color: var(--app-text, #64748b);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quick-action-item__arrow {
  font-size: 0.75rem;
  color: var(--app-text, #94a3b8);
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;
}

.quick-action-item:hover .quick-action-item__arrow {
  opacity: 1;
  transform: translateX(0);
}

/* Animations */
.fab-backdrop-fade-enter-active,
.fab-backdrop-fade-leave-active {
  transition: opacity 0.25s ease;
}

.fab-backdrop-fade-enter-from,
.fab-backdrop-fade-leave-to {
  opacity: 0;
}

.fab-menu-pop-enter-active {
  transition: all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fab-menu-pop-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.fab-menu-pop-enter-from {
  opacity: 0;
  transform: scale(0.85) translateY(20px);
}

.fab-menu-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(14px);
}

/* Quick File Upload Dialog */
.quick-file-policy-pill {
  background: var(--app-surface-muted, #f8fafc);
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: 10px;
}

.quick-file-dropzone {
  width: 100%;
}

:deep(.quick-file-dropzone .el-upload) {
  width: 100%;
}

:deep(.quick-file-dropzone .el-upload-dragger) {
  width: 100%;
  padding: 32px 20px;
  border-radius: 12px;
  border: 2px dashed var(--app-border-strong, #cbd5e1);
  background: var(--app-surface-soft, #fafbfc);
  transition: all 0.25s ease;
}

:deep(.quick-file-dropzone .el-upload-dragger:hover) {
  border-color: var(--bs-info, #0b4f9c);
  background: var(--app-surface-tint, #f0f7ff);
}

.quick-file-dropzone__icon {
  font-size: 2.75rem;
  color: var(--bs-info, #0b4f9c);
  margin-bottom: 8px;
  display: inline-block;
}

.quick-file-dropzone__title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--app-heading, #1e293b);
  margin-bottom: 4px;
}

/* Responsive adjustment */
@media (max-width: 768px) {
  .quick-actions-fab-container {
    right: 16px;
    bottom: 16px;
  }
  .quick-actions-fab-btn {
    width: 52px;
    height: 52px;
  }
  .quick-actions-menu {
    bottom: 64px;
    width: calc(100vw - 32px);
    right: -4px;
  }
}
</style>
